import { HttpInterceptorFn, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { TenantContextService } from '../services/tenant-context.service';
import { environment } from '../../environments/environment';

/**
 * AdminApiBearerInterceptor — auto-injects the in-memory access_token as a
 * `Authorization: Bearer ...` header on every request to the admin API.
 *
 * Admin-API recognition uses URL parsing (M6 Opus RP7) — the pre-fix
 * `startsWith` check accepted path-traversal segments like
 * `/admin/../public` and could leak the Bearer header to non-admin paths.
 *
 * Local-dev mode (M4 Opus RP7 Round 3): when `environment.tenantAdminApiUrl`
 * is empty (the `local` build configuration), HttpClient emits relative
 * paths that resolve against the page origin. SPA routes share the
 * `/admin/` prefix with backend API paths (`/admin/environments` is
 * BOTH a route AND a future API endpoint), so a generic `/admin/` match
 * would attach the Bearer to accidental SPA-URL XHRs and leak it through
 * the dev-proxy debug log. Fix: an explicit allowlist of API paths.
 * Add new paths here as the backend grows new admin endpoints.
 *
 * 401 / 403 response handling (S22 Opus RP7 + S5 Round 3 + post-RP8c
 * refresh-redemption):
 *   - 401: first try `auth.refresh()` — if the operator has a refresh
 *     token (post-RP8c offline_access grant) and the backend accepts it,
 *     replay the original request with the new Bearer header so the
 *     operator doesn't see the 401. Only if refresh returns false (no
 *     refresh_token, refresh expired/revoked, network error) do we run
 *     the existing logout + snackbar + /signin flow. The S5 single-burst
 *     guard (`auth.isAuthenticated()`) still wraps the logout side-effects
 *     so concurrent 401s don't stack snackbars.
 *   - 403: redirect to /permission-denied (token valid but scope insufficient).
 */

// M4 (Round 3) — explicit allowlist of backend admin-API path prefixes.
// Only these paths get the Bearer header attached under local-dev mode
// (where SPA routes and API paths share the `/admin/` prefix on the
// page origin). Production mode uses an origin-based check that doesn't
// need the allowlist because the SPA origin (admin.X.emjo-consulting.dk)
// and API origin (oauth.X.emjo-consulting.dk) are different hosts.
// Exported so the regression spec can assert every admin-API family the SPA
// calls is covered. `/admin/operators` added in Story 21.2 (operator invite) —
// without it, POST /admin/operators/invite went out with no Bearer in local
// mode and the backend returned 401 "missing or invalid bearer token".
// `/admin/preferences` added in Story 25.4 (operator theme preference) — without
// it, GET/PATCH /admin/preferences went out with no Bearer in local-dev mode.
// `/admin/mfa` added in Story 47.1 (operator MFA) — the whole enrollment,
// re-auth, recovery-code and disable family lives under it. It was MISSED when
// the family landed and caught in the 47.1 code review: deployed environments
// take the origin-based branch below and never consult this list, so the gap was
// invisible on dev and stage and broke `ng serve` only. That is the third time
// this list has gone stale (21.2, 25.4, 47.1) — if it goes stale a fourth time,
// derive it from the API service instead of maintaining it by hand.
export const LOCAL_ADMIN_API_PATH_PREFIXES = ['/admin/tenant', '/admin/environments', '/admin/operators', '/admin/preferences', '/admin/compliance', '/admin/terms', '/admin/mfa'];

export const adminApiInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const tenantContext = inject(TenantContextService);
  const router = inject(Router);
  const snackBar = inject(MatSnackBar);

  const isAdminApi = isAdminApiRequest(req.url);
  let request = req;
  if (isAdminApi) {
    const token = auth.currentAccessToken();
    if (token) {
      request = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
    }
  }

  return next(request).pipe(
    catchError((err: HttpErrorResponse) => {
      if (isAdminApi && err.status === 401) {
        // Post-RP8c — try refresh-token redemption first; the auth
        // service single-flights concurrent calls so parallel 401s
        // from in-flight admin XHRs all await the same refresh.
        return from(auth.refresh()).pipe(
          switchMap((refreshed): Observable<HttpEvent<unknown>> => {
            if (refreshed) {
              const newToken = auth.currentAccessToken();
              if (newToken === null) {
                // Defensive: refresh said success but the session is
                // empty — fall back to logout.
                return fallToSignIn(err);
              }
              // Story 41.10 (M13) — X-Retry marks the retried request so it's
              // identifiable in logs/traces as a post-refresh replay, not a
              // first attempt. It carries no gating logic here: the catchError
              // below already routes ANY error on this retry — 401 or
              // otherwise — straight to fallToSignIn, no second refresh,
              // purely by virtue of its position in the pipe. (Code review:
              // an earlier version added a header-check "guard" at the top of
              // this branch, but it inspected the outer request object, which
              // never carries this header, so it could never fire — removed
              // as dead code; the catchError below was always the real and
              // sufficient enforcement point.)
              const retry = req.clone({
                setHeaders: { Authorization: `Bearer ${newToken}`, 'X-Retry': '1' },
              });
              return next(retry);
            }
            return fallToSignIn(err);
          }),
          catchError(() => fallToSignIn(err)),
        );
      }
      if (isAdminApi && err.status === 403) {
        void router.navigateByUrl('/permission-denied');
      }
      return throwError(() => err);
    }),
  );

  function fallToSignIn(originalErr: HttpErrorResponse): Observable<HttpEvent<unknown>> {
    // S5 single-burst guard — only the first 401-after-refresh-failure
    // runs the side effects; subsequent ones see the already-cleared
    // session and short-circuit. Keeps snackbar count at 1 + avoids
    // navigation thrash when many in-flight XHRs all 401 simultaneously.
    if (auth.isAuthenticated()) {
      snackBar.open('Your session expired. Please sign in again.', undefined, { duration: 5000 });
      auth.logout();
      tenantContext.clear();
      void router.navigateByUrl('/signin');
    }
    return throwError(() => originalErr);
  }
};

function isAdminApiRequest(url: string): boolean {
  let parsed: URL;
  try {
    // Resolve relative URLs against the current page origin so dev-mode
    // calls (e.g. '/admin/tenant' → 'http://localhost:4200/admin/tenant')
    // parse cleanly instead of throwing.
    parsed = new URL(url, window.location.origin);
  } catch {
    return false;
  }
  if (!environment.tenantAdminApiUrl) {
    // Local-dev mode — explicit allowlist (M4 Round 3) prevents Bearer
    // attachment to SPA-route URL strings that happen to share the
    // `/admin/` prefix.
    if (parsed.origin !== window.location.origin) {
      return false;
    }
    return LOCAL_ADMIN_API_PATH_PREFIXES.some(
      (prefix) => parsed.pathname === prefix || parsed.pathname.startsWith(prefix + '/'),
    );
  }
  let adminBase: URL;
  try {
    adminBase = new URL(environment.tenantAdminApiUrl);
  } catch {
    return false;
  }
  // Production mode — origin separation between SPA + API hosts means
  // any `/admin/*` path on the API origin is genuinely an API call.
  return parsed.origin === adminBase.origin && parsed.pathname.startsWith('/admin/');
}
